import { AxiosError } from 'axios';
import { parseGenerationSummary, type GenerationSummary } from '@entities/Generation';
import { api } from '@shared/api';
import { mapSenseContexts, newUuid } from '@shared/lib';
import type { Level, WordMeaning } from '@shared/types';
import { forgetLookup } from '../lib/lookupCache';
import { canonicalPos } from '../lib/pos';
import { generationFailureMessage } from '../lib/generationFailure';
import {
  parseCreateError,
  parseRegenerateError,
  type CreateErrorCode,
  type CreateErrorModule,
  type QuotaLimitInfo,
  type RegenerateErrorCode,
} from '../lib/createErrors';
import { mockCreateCard, mockFetchPendingCards } from './mockWorkspace';

export type { QuotaLimitInfo };
import type {
  WorkspaceCard,
  WorkspaceCardProblem,
  WorkspaceCardReady,
  WorkspaceCardStatus,
} from '../model/types';

const USE_MOCK = (import.meta.env.VITE_CARDS_WORKSPACE_MOCK ?? 'false') !== 'false';

export interface CreateCardArgs {
  front: string;
  moduleId: string;
  moduleTitle: string;
  difficulty: Level;
  /** Saved topic controls examples; ownership is checked by the backend. */
  topicBatchId?: string;
  /** Выбранные переводы слова: каждый — своё значение карточки. Генерация ОДНА на всё
   *  нажатие. Пусто и нет фраз → бэк возьмёт все части речи (прежнее поведение). */
  picks?: { pos: string | null; translation: string }[];
  /** Выбранные фразы и фразовые глаголы (2026-10-06): своя карточка на каждую, создаётся
   *  тем же нажатием. Есть фразы и нет `picks` — карточки самого слова нет. */
  phrases?: { front: string; picks: { pos: string | null; translation: string }[] }[];
  /** Другие слова той же подборки по теме (только с `topicBatchId`): пачка до 10 значений
   *  от разных слов — одно нажатие и одна AI-генерация. */
  words?: { front: string; picks: { pos: string | null; translation: string }[] }[];
  /** Id нажатия (`generation_id`, ТЗ главной § 22.12): одно нажатие — одна запись «Генераций».
   *  Не передан — новый на каждый вызов (так работает `/cards/new`). Главная передаёт свой:
   *  после обрыва связи тот же id и то же тело сервер повторит без второго жетона. */
  generationId?: string;
  /** Номер пачки того же нажатия (подбор по теме), 0–49; по умолчанию 0. */
  generationPart?: number;
}

/** Ответ создания: карточка слова (или первой фразы) и карточки остальных фраз. */
export interface CreateCardsResult {
  cards: WorkspaceCard[];
  /** Слова и фразы, карточки которых не создались (уже учатся, нет в словаре…). */
  skipped: string[];
  /** Запись нажатия в «Генерациях»; null — ничего не создано и не сохранено. */
  generation: GenerationSummary | null;
  /** Сервер повторил прежний ответ на тот же id и тело — без второго жетона и дублей. */
  replayed: boolean;
}

/** Коды ошибок создания (разбор — `parseCreateError`) и пустые поля формы. */
export type CreateCardErrorCode = 'EMPTY_FIELDS' | CreateErrorCode;

// --- Живой словарный поиск перевода (GET /cards/lookup) ---------------------

/** Одно значение в превью (2026-10-06): перевод слова, фразы или фразового глагола.
 *  Выбранное значение — отдельная карточка, главный перевод — первый. */
export interface LookupSense {
  /** Ключ значения в ответе (меняется при перезаливке словаря — не хранить). */
  id: string;
  /** Часть речи карточки: noun/verb/…; phrase — идиома, verb — фразовый глагол. */
  pos: string | null;
  /** core | common | rare; null — Яндекс. */
  frequency: string | null;
  main: string;
  translations: string[];
  /** Как употреблять (по-русски). */
  usageNote: string | null;
  /** Пометы Oxford: стиль (informal…), регион (British…), грамматика (usually plural…). */
  registers: string[];
  region: string[];
  grammar: string[];
  example: { en: string; ru: string } | null;
  /** Ученик это значение уже учит в модуле. */
  learned: boolean;
  /** Значение выбрано и ждёт генерацию. */
  generating: boolean;
  /** true — своё значение в готовой карточке, без генерации; false — допишет AI;
   *  null — готовой карточки нет. */
  ready: boolean | null;
}

/** Фраза или фразовый глагол слова — отдельная карточка на саму фразу (`query`). */
export interface LookupConstruction {
  id: string;
  kind: 'idiom' | 'phrasal_verb';
  /** Как в словаре: be down on your luck, go through something. */
  phrase: string;
  /** Слово будущей карточки и поиска: go through. */
  query: string;
  pos: string | null;
  frequency: string | null;
  registers: string[];
  region: string[];
  grammar: string[];
  usageNote: string | null;
  senses: LookupSense[];
}

export interface LookupResult {
  /** Есть ли слово в словаре. false → опечатка / нет статьи. */
  found: boolean;
  frontName: string;
  transcription: string | null;
  /** nbars — словарь владельца, yandex — запасной (без помет). */
  source: 'nbars' | 'yandex' | null;
  /** Четыре раздела (решение владельца 2026-10-06). */
  active: LookupSense[];
  other: LookupSense[];
  phrases: LookupConstruction[];
  phrasalVerbs: LookupConstruction[];
}

// Ответ бэка после snake-case интерцептора.
interface BackendLookupSense {
  id: string;
  pos: string | null;
  frequency: string | null;
  main: string | null;
  translations: string[];
  usage_note?: string | null;
  registers?: string[];
  region?: string[];
  grammar?: string[];
  example?: { en: string; ru: string } | null;
  learned?: boolean;
  generating?: boolean;
  ready?: boolean | null;
}

interface BackendLookupConstruction {
  id: string;
  kind: string;
  phrase: string;
  query: string;
  pos: string | null;
  frequency: string | null;
  usage_note?: string | null;
  registers?: string[];
  region?: string[];
  grammar?: string[];
  senses?: BackendLookupSense[];
}

export interface BackendLookupResult {
  found: boolean;
  front_name: string;
  transcription: string | null;
  source?: string | null;
  active?: BackendLookupSense[];
  other?: BackendLookupSense[];
  phrases?: BackendLookupConstruction[];
  phrasal_verbs?: BackendLookupConstruction[];
}

function mapSense(s: BackendLookupSense): LookupSense | null {
  const translations = (s.translations ?? []).filter(Boolean);
  const main = s.main || translations[0];
  if (!main) return null;
  return {
    id: s.id,
    pos: s.pos ?? null,
    frequency: s.frequency ?? null,
    main,
    translations: translations.length ? translations : [main],
    usageNote: s.usage_note ?? null,
    registers: s.registers ?? [],
    region: s.region ?? [],
    grammar: s.grammar ?? [],
    example: s.example?.en && s.example?.ru ? { en: s.example.en, ru: s.example.ru } : null,
    learned: !!s.learned,
    generating: !!s.generating,
    ready: s.ready ?? null,
  };
}

function mapSenses(list: BackendLookupSense[] | undefined): LookupSense[] {
  return (list ?? []).map(mapSense).filter((s): s is LookupSense => s !== null);
}

function mapConstructions(list: BackendLookupConstruction[] | undefined): LookupConstruction[] {
  return (list ?? [])
    .map((c) => ({
      id: c.id,
      kind: (c.kind === 'phrasal_verb' ? 'phrasal_verb' : 'idiom') as LookupConstruction['kind'],
      phrase: c.phrase,
      query: c.query,
      pos: c.pos ?? null,
      frequency: c.frequency ?? null,
      registers: c.registers ?? [],
      region: c.region ?? [],
      grammar: c.grammar ?? [],
      usageNote: c.usage_note ?? null,
      senses: mapSenses(c.senses),
    }))
    .filter((c) => c.query && c.senses.length > 0);
}

/** Живой поиск перевода во время ввода. Дебаунс делается в useTranslationLookup.
 *  Квоту НЕ тратит. found=false ⇒ AI-генерация недоступна (нет словарной статьи). */
export async function lookupTranslation(
  front: string,
  moduleId?: string | null,
  signal?: AbortSignal,
): Promise<LookupResult> {
  const params: Record<string, string> = { front_name: front.trim() };
  if (moduleId) params.module_id = moduleId;
  const { data } = await api.get<BackendLookupResult>('/cards/lookup', { params, signal });
  return mapLookupResult(data, front);
}

export function mapLookupResult(data: BackendLookupResult, fallbackFront = ''): LookupResult {
  const result: LookupResult = {
    found: !!data.found,
    frontName: data.front_name ?? fallbackFront.trim().toLowerCase(),
    transcription: data.transcription ?? null,
    source: data.source === 'yandex' ? 'yandex' : data.source ? 'nbars' : null,
    active: mapSenses(data.active),
    other: mapSenses(data.other),
    phrases: mapConstructions(data.phrases),
    phrasalVerbs: mapConstructions(data.phrasal_verbs),
  };
  // Найдено, но выбирать нечего (старый бэк без разделов) — как «нет в словаре».
  result.found =
    result.found &&
    result.active.length + result.other.length + result.phrases.length + result.phrasalVerbs.length > 0;
  return result;
}

export type CreateCardErrorModule = CreateErrorModule;

export class CreateCardError extends Error {
  /** Для ALREADY_ADDED — список модулей юзера, где уже есть это слово.
   *  Используется в тосте чтобы точно показать «уже в модуле X», а не общее
   *  «в этом модуле» (юзер может быть сейчас в другом). */
  public modules: CreateCardErrorModule[];
  /** Для DAILY_LIMIT_REACHED — лимит/использовано/сброс из 429-ответа. */
  public quota?: QuotaLimitInfo;
  /** Для GENERATION_PART_IN_PROGRESS — через сколько проверить, создались ли карточки. */
  public retryAfterMs?: number;
  /** `error.code` сервера, если был. */
  public serverCode: string | null;
  constructor(
    public code: CreateCardErrorCode,
    modules: CreateCardErrorModule[] = [],
    quota?: QuotaLimitInfo,
    extra: { retryAfterMs?: number; serverCode?: string | null } = {},
  ) {
    super(code);
    this.name = 'CreateCardError';
    this.modules = modules;
    this.quota = quota;
    this.retryAfterMs = extra.retryAfterMs;
    this.serverCode = extra.serverCode ?? null;
  }
}

/** Ответ с ошибкой создания или ручной карточки → типизированная ошибка (`parseCreateError`). */
function toCreateCardError(err: unknown): CreateCardError {
  if (!(err instanceof AxiosError)) return new CreateCardError('UNKNOWN');
  const parsed = parseCreateError(err.response ? err.response.status : null, err.response?.data);
  return new CreateCardError(parsed.code, parsed.modules, parsed.quota, {
    retryAfterMs: parsed.retryAfterMs,
    serverCode: parsed.serverCode,
  });
}

/** Запись нажатия и признак повтора из ответа создания (поля появились с «Генерациями»). */
function generationFields(data: { generation?: unknown; replayed?: unknown }) {
  return {
    generation: data.generation ? parseGenerationSummary(data.generation) : null,
    replayed: data.replayed === true,
  };
}

/** Список сводок записей из ответа ручки (`generations` у повтора, `released` у ручной карточки — контракт
 *  § 7.1, этап 4). Поля нет (старый бэкенд) — `null`: о записях ответ ничего не говорит. */
function generationList(value: unknown): GenerationSummary[] | null {
  if (!Array.isArray(value)) return null;
  return value.map(parseGenerationSummary).filter((summary): summary is GenerationSummary => summary !== null);
}

// Бэк отдаёт CardSchema, axios-интерцептор приводит ключи к snake_case.
interface BackendTranslation {
  id: string;
  text: string;
  gender: string | null;
}

interface BackendSense {
  id: string;
  translations: BackendTranslation[];
  en_synonyms: string[];
  example_en: string | null;
  simple_example_en?: string | null;
  simple_example_ru?: string | null;
  hint: string | null;
  definition: string | null;
  selection_options: string[] | null;
  is_rare?: boolean;
  contexts?: unknown;
}

interface BackendMeaning {
  id: string;
  pos: string | null;
  senses: BackendSense[];
}

export interface BackendCard {
  id: string;
  front_name: string;
  /** Фразовый глагол — помечается при генерации (2026-10-06). */
  is_phrasal_verb?: boolean;
  primary_translation: string | null;
  transcription: string | null;
  cefr: Level | null;
  meanings: BackendMeaning[];
  is_need_error_action?: boolean;
  error_info?: string | string[] | null;
  expect_word?: string[] | null;
  status?:
    | 'pending'
    | 'in_progress'
    | 'completed'
    | 'rejected'
    | 'failed'
    | 'manual'
    | string;
  /** Только в ответе POST /cards/create: карточка готова, но выбранная часть речи
   *  дописывается генерацией — строка ждёт SSE. */
  generation_queued?: boolean | null;
}

const KNOWN_LEVELS: ReadonlyArray<Level> = ['A2', 'B1', 'B2', 'C1'];

function toLevel(value: string | null | undefined, fallback: Level): Level {
  if (!value) return fallback;
  const upper = value.toUpperCase();
  return (KNOWN_LEVELS as readonly string[]).includes(upper) ? (upper as Level) : fallback;
}

function normalizeErrorInfo(value: string | string[] | null | undefined): string | null {
  if (value == null) return null;
  if (typeof value === 'string') return value.trim() || null;
  if (Array.isArray(value)) {
    const joined = value.join('').trim();
    return joined || null;
  }
  return null;
}

function normalizeExpectWord(value: string[] | null | undefined): string[] {
  if (!Array.isArray(value) || value.length === 0) return [];

  const looksCharSplit =
    value.length >= 3 &&
    value[0] === '{' &&
    value[value.length - 1] === '}' &&
    value.every((c) => typeof c === 'string' && c.length <= 1);

  if (looksCharSplit) {
    const inner = value.slice(1, -1).join('');
    return inner
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  return value.filter((v) => typeof v === 'string' && v.trim().length > 0);
}

export function mapBackendMeanings(meanings: BackendMeaning[] | null | undefined): WordMeaning[] {
  if (!Array.isArray(meanings)) return [];
  return meanings.map((m) => ({
    id: m.id,
    pos: m.pos ?? null,
    senses: (m.senses ?? []).map((s) => ({
      id: s.id,
      translations: (s.translations ?? []).map((t) => ({
        id: t.id,
        text: t.text,
        gender: t.gender ?? null,
      })),
      enSynonyms: s.en_synonyms ?? [],
      exampleEn: s.example_en ?? null,
      simpleExampleEn: s.simple_example_en ?? null,
      simpleExampleRu: s.simple_example_ru ?? null,
      hint: s.hint ?? null,
      definition: s.definition ?? null,
      selectionOptions: s.selection_options ?? null,
      isRare: s.is_rare ?? false,
      contexts: mapSenseContexts(s.contexts),
    })),
  }));
}

/** Достаёт первичный перевод. Сначала primary_translation, иначе первый перевод первого sense. */
export function pickPrimaryTranslation(card: BackendCard): string {
  if (card.primary_translation && card.primary_translation.trim()) {
    return card.primary_translation.trim();
  }
  const first = card.meanings?.[0]?.senses?.[0]?.translations?.[0]?.text;
  return first?.trim() ?? '';
}

function buildReady(card: BackendCard, fallback: Level): WorkspaceCardReady {
  return {
    transcription: card.transcription ?? null,
    cefr: toLevel(card.cefr, fallback),
    meanings: mapBackendMeanings(card.meanings),
  };
}

interface MappedCard {
  status: WorkspaceCardStatus;
  ready?: WorkspaceCardReady;
  problem?: WorkspaceCardProblem;
}

function mapBackendCardToState(
  card: BackendCard,
  fallbackCefr: Level,
  selectedTranslationIds: string[],
): MappedCard {
  const errorInfo = normalizeErrorInfo(card.error_info);
  const expectWord = normalizeExpectWord(card.expect_word);

  // Generation failures also set is_need_error_action. Check them first so
  // reload/polling keeps the same retry action as the live SSE path.
  const failureMessage = generationFailureMessage(card.status, errorInfo);
  if (failureMessage !== null) {
    return {
      status: 'error',
      problem: {
        message: failureMessage,
        suggestions: [],
      },
    };
  }

  if (card.is_need_error_action) {
    if (expectWord.length > 0) {
      return {
        status: 'typo',
        problem: {
          message: errorInfo ?? 'Похоже, в слове опечатка.',
          suggestions: expectWord,
        },
      };
    }
    return {
      status: 'garbage',
      problem: {
        message: errorInfo ?? 'Не удалось распознать слово.',
        suggestions: [],
      },
    };
  }

  if (card.status === 'manual') {
    // Ручная карточка сразу готова: содержимое ввёл сам пользователь, единственный
    // перевод предотмечен в selected_translation_ids → не показываем «в очереди»
    // и не зовём на выбор переводов. Сразу ready, в сессии работает базовый «угадай».
    return {
      status: 'ready',
      ready: buildReady(card, fallbackCefr),
    };
  }

  if (card.status === 'completed') {
    // Карточка догенерилась. Пустой selectedTranslationIds — НЕ «юзер должен выбрать
    // переводы» (этот шаг удалён: сервер авто-выбирает все части речи), а окно гонки
    // между коммитом воркера (cards.status=COMPLETED) и коммитом авто-выбора в
    // notify_waiting_users. Показываем «в очереди» — ближайший рефетч доведёт до ready.
    if (selectedTranslationIds.length === 0) return { status: 'queued' };
    return {
      status: 'ready',
      ready: buildReady(card, fallbackCefr),
    };
  }

  if (card.status === 'rejected') {
    return {
      status: 'error',
      problem: {
        message: errorInfo ?? 'Не удалось завершить генерацию.',
        suggestions: [],
      },
    };
  }

  // pending / in_progress / unknown — карточка ещё в процессе
  return { status: 'queued' };
}

export type UserStateCardServerStatus = 'is_need_action' | 'pending' | 'success';

export interface FetchPendingCardsOpts {
  moduleId?: string;
  moduleTitle?: string;
  /** Серверный фильтр по статусу `user_card_state`. Если не задан — отдаются все. */
  status?: UserStateCardServerStatus | UserStateCardServerStatus[];
  /** Серверный фильтр по способу создания (llm_generated / manual). */
  creationMethod?: 'manual' | 'llm_generated';
  page?: number;
  size?: number;
}

interface BackendUserStateCard {
  /** surrogate user_card_state.id — уникален для строки */
  id: string;
  card_id: string;
  /** meaning_id состояния. NULL = pending state (юзер ещё не выбрал переводы). */
  meaning_id: string | null;
  /** Shortcut для удобства фронта — pos из card.meanings[meaning_id]. NULL для pending. */
  pos: string | null;
  card: BackendCard;
  selected_translation_ids?: string[] | null;
  /** Смыслы, отмеченные для тренировки (основной, если ничего не отмечено). */
  session_sense_ids?: string[] | null;
  /** Для pending state: выбранные строки подсказок, которые ещё генерируются. */
  pending_picks?: { pos: string | null; translation: string | null }[] | null;
  /** Последнее действие ученика с карточкой (ISO) — очередь «последние изменения сверху». */
  touched_at?: string | null;
}

interface BackendPage<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface PendingCardsResult {
  items: WorkspaceCard[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export async function fetchPendingCardsPaged(
  opts: FetchPendingCardsOpts = {},
): Promise<PendingCardsResult> {
  if (USE_MOCK) {
    const items = await mockFetchPendingCards();
    return { items, total: items.length, page: 1, size: items.length, pages: 1 };
  }

  const moduleId = opts.moduleId ?? '';
  const moduleTitle = opts.moduleTitle ?? '';

  const params: Record<string, string | number | string[]> = {
    page: opts.page ?? 1,
    size: opts.size ?? 100,
  };
  if (opts.moduleId) params.module_id = opts.moduleId;
  if (opts.status) params.status = opts.status;
  if (opts.creationMethod) params.creation_method = opts.creationMethod;

  try {
    const { data } = await api.get<BackendPage<BackendUserStateCard>>(
      '/cards/progress-cards',
      { params },
    );

    const items: WorkspaceCard[] = data.items.map((entry) => {
      const card = entry.card;
      const fallbackCefr = toLevel(card.cefr, 'A2');
      const selectedTranslationIds = entry.selected_translation_ids ?? [];
      const mapped = mapBackendCardToState(card, fallbackCefr, selectedTranslationIds);
      // Строка ожидания показывает выбранную часть речи, а не primary карточки: у
      // дописываемого глагола это был бы перевод уже изучаемого наречия.
      const waitingPick = entry.meaning_id === null ? entry.pending_picks?.[0] : undefined;
      return {
        stateId: entry.id,
        cardId: card.id,
        meaningId: entry.meaning_id,
        pos: entry.pos ?? waitingPick?.pos ?? null,
        status: mapped.status,
        payload: {
          front: card.front_name,
          back: waitingPick?.translation || pickPrimaryTranslation(card),
          moduleId,
          moduleTitle,
          difficulty: fallbackCefr,
        },
        // Порядок очереди — по последнему изменению с сервера (2026-10-06). Раньше здесь
        // было Date.now(): все строки списка получали «сейчас», а только что созданные
        // хранили время нажатия — и уезжали вниз.
        createdAt: (entry.touched_at && Date.parse(entry.touched_at)) || Date.now(),
        ready: mapped.ready,
        problem: mapped.problem,
        selectedTranslationIds,
        sessionSenseIds: entry.session_sense_ids ?? [],
        isManual: card.status === 'manual',
        isPhrasalVerb: !!card.is_phrasal_verb,
        generatingCount: entry.meaning_id === null ? entry.pending_picks?.length || undefined : undefined,
        picks:
          entry.meaning_id === null && entry.pending_picks?.length
            ? entry.pending_picks
                .filter((p): p is { pos: string | null; translation: string } => Boolean(p.translation))
                .map((p) => ({ pos: p.pos, translation: p.translation }))
            : undefined,
      };
    });

    return {
      items,
      total: data.total,
      page: data.page,
      size: data.size,
      pages: data.pages,
    };
  } catch {
    return { items: [], total: 0, page: 1, size: 0, pages: 0 };
  }
}

/** Совместимый хелпер: возвращает только items (тонкая обёртка над paged). */
export async function fetchPendingCards(
  opts: FetchPendingCardsOpts = {},
): Promise<WorkspaceCard[]> {
  const res = await fetchPendingCardsPaged(opts);
  return res.items;
}

export interface PendingCardsStats {
  all: number;
  /** Создано с помощью ИИ (все статусы) = total − manual: у creation_method два значения. */
  ai: number;
  generating: number;
  manual: number;
  /** НЕ фильтры — данные для инфо-строки «N ждут тебя · M готово» справа от пилюль. */
  problems: number;
  ready: number;
}

interface BackendCardsStats {
  success: number;
  pending: number;
  is_need_action: number;
  manual: number;
  total: number;
}

export async function fetchProgressCardsStats(moduleId?: string): Promise<PendingCardsStats> {
  try {
    const { data } = await api.get<BackendCardsStats>('/cards/stats', {
      params: moduleId ? { module_id: moduleId } : undefined,
    });
    return {
      all: data.total,
      ai: Math.max(0, data.total - data.manual),
      generating: data.pending,
      manual: data.manual,
      problems: data.is_need_action,
      ready: data.success,
    };
  } catch {
    return { all: 0, ai: 0, generating: 0, manual: 0, problems: 0, ready: 0 };
  }
}

export type DeleteCardErrorCode = 'NETWORK' | 'UNKNOWN';

export class DeleteCardError extends Error {
  constructor(public code: DeleteCardErrorCode) {
    super(code);
    this.name = 'DeleteCardError';
  }
}

/**
 * Убирает карточку из учётки пользователя: user_card_state + связи в его модулях
 * + висящие card_generation_requests. Сама строка `cards` остаётся.
 * Идемпотентно — 204 даже если ничего не было удалено.
 *
 * Если передан `meaningId` — снимается ТОЛЬКО конкретная часть речи (per-pos state).
 * Если `meaningId === null` (pending state) или undefined — сносятся все state'ы юзера
 * по этой карточке.
 */
export async function deleteCard(cardId: string, meaningId?: string | null): Promise<void> {
  try {
    await api.delete(`/cards/${cardId}`, {
      params: meaningId ? { meaning_id: meaningId } : undefined,
    });
  } catch (err) {
    if (err instanceof AxiosError) {
      if (!err.response) throw new DeleteCardError('NETWORK');
    }
    throw new DeleteCardError('UNKNOWN');
  }
}

/**
 * «Скрыть из модуля» — soft-hide вместо удаления: state, переводы, связь и
 * FSRS-прогресс сохраняются, карточку можно вернуть из вкладки «Скрытые» на
 * странице модуля. Применяется к готовым (real-state) карточкам; meaningId обязателен.
 * Реюзит DeleteCardError для единообразной обработки ошибок на странице.
 */
export async function hideCard(cardId: string, meaningId: string): Promise<void> {
  try {
    await api.post(`/cards/${cardId}/hide`, undefined, { params: { meaning_id: meaningId } });
  } catch (err) {
    if (err instanceof AxiosError) {
      if (!err.response) throw new DeleteCardError('NETWORK');
    }
    throw new DeleteCardError('UNKNOWN');
  }
}

export interface CardEditMeaningState {
  meaningId: string;
  pos: string | null;
  selectedTranslationIds: string[];
  hasProgress: boolean;
}

export interface CardEditData {
  /** Полная карточка со всеми meanings/senses/translations (без projection per-state). */
  card: {
    id: string;
    front_name: string;
    transcription: string | null;
    cefr: Level | null;
    meanings: BackendMeaning[];
  };
  meaningsState: CardEditMeaningState[];
}

export type FetchEditDataErrorCode =
  | 'CARD_STATE_NOT_FOUND'
  | 'CARD_NOT_FOUND'
  | 'NETWORK'
  | 'UNKNOWN';

export class FetchEditDataError extends Error {
  constructor(public code: FetchEditDataErrorCode) {
    super(code);
    this.name = 'FetchEditDataError';
  }
}

interface BackendCardEditData {
  card: BackendCard;
  meanings_state: Array<{
    meaning_id: string;
    pos: string | null;
    selected_translation_ids: string[];
    has_progress: boolean;
  }>;
}

/** Тянет полный словарь карточки + текущий выбор и FSRS-метки по каждой pos.
 *  Используется для модалки «Редактировать переводы». */
export async function fetchCardEditData(cardId: string): Promise<CardEditData> {
  try {
    const { data } = await api.get<BackendCardEditData>(`/cards/${cardId}/edit-data`);
    return {
      card: {
        id: data.card.id,
        front_name: data.card.front_name,
        transcription: data.card.transcription,
        cefr: data.card.cefr,
        meanings: data.card.meanings,
      },
      meaningsState: data.meanings_state.map((m) => ({
        meaningId: m.meaning_id,
        pos: m.pos,
        selectedTranslationIds: m.selected_translation_ids,
        hasProgress: m.has_progress,
      })),
    };
  } catch (err) {
    if (err instanceof AxiosError) {
      const code = err.response?.data?.error?.code;
      if (code === 'CARD_STATE_NOT_FOUND') throw new FetchEditDataError('CARD_STATE_NOT_FOUND');
      if (code === 'CARD_NOT_FOUND') throw new FetchEditDataError('CARD_NOT_FOUND');
      if (!err.response) throw new FetchEditDataError('NETWORK');
    }
    throw new FetchEditDataError('UNKNOWN');
  }
}

export type ReportCardErrorCode =
  | 'CARD_STATE_NOT_FOUND'
  | 'CARD_NOT_FOUND'
  | 'CARD_NOT_REPORTABLE'
  | 'NETWORK'
  | 'UNKNOWN';

export class ReportCardError extends Error {
  constructor(public code: ReportCardErrorCode) {
    super(code);
    this.name = 'ReportCardError';
  }
}

/**
 * Сообщает о неправильном переводе в AI-карточке. Идемпотентно: повторный
 * вызов от того же юзера обновит reason на бэке.
 * Бэк отвечает 409 CARD_NOT_REPORTABLE для manual / в работе / failed карточек.
 */
export async function reportCardTranslation(
  cardId: string,
  reason?: string | null,
): Promise<void> {
  try {
    await api.post(`/cards/${cardId}/report`, {
      reason: reason && reason.trim() ? reason.trim() : null,
    });
  } catch (err) {
    if (err instanceof AxiosError) {
      const code = err.response?.data?.error?.code;
      if (code === 'CARD_STATE_NOT_FOUND') throw new ReportCardError('CARD_STATE_NOT_FOUND');
      if (code === 'CARD_NOT_FOUND') throw new ReportCardError('CARD_NOT_FOUND');
      if (code === 'CARD_NOT_REPORTABLE') throw new ReportCardError('CARD_NOT_REPORTABLE');
      if (!err.response) throw new ReportCardError('NETWORK');
    }
    throw new ReportCardError('UNKNOWN');
  }
}

/** Коды ошибок «Повторить» (разбор — `parseRegenerateError`): RATE_LIMIT — 429 без кода жетонов,
 *  SERVER — 5xx, исход неизвестен. */
export type RegenerateCardErrorCode = RegenerateErrorCode;

export class RegenerateCardError extends Error {
  /** Для DAILY_LIMIT_REACHED — детали квоты из 429-ответа. */
  public quota?: QuotaLimitInfo;
  constructor(public code: RegenerateCardErrorCode, quota?: QuotaLimitInfo) {
    super(code);
    this.name = 'RegenerateCardError';
    this.quota = quota;
  }
}

/** Ответ повтора: строка очереди и сводки записей «Генераций» с этой карточкой сразу после запуска. */
export interface RegenerateCardResult {
  card: WorkspaceCard;
  /** Контракт § 7.1, этап 4: обычно одна запись, её элемент — «генерируется»; `null` — старый бэкенд. */
  generations: GenerationSummary[] | null;
}

/**
 * Запускает AI-генерацию по уже существующей карточке (manual / failed / rejected).
 * Бэк сносит её содержимое, переводит в IN_PROGRESS и ставит в очередь worker'а.
 * После SSE-эвента `succeeded` карточка станет `ready` (сервер авто-выбирает переводы).
 * Вместе с карточкой — сводки её записей «Генераций» («Повторить» в записи на главной).
 */
export async function regenerateCardWithGenerations(cardId: string, moduleId: string): Promise<RegenerateCardResult> {
  try {
    const { data } = await api.post<BackendCard & { generations?: unknown }>(`/cards/${cardId}/regenerate`, {
      module_id: moduleId,
    });
    // Карточка после регенерации сразу IN_PROGRESS, selectedTranslationIds пуст —
    // mapBackendCardToState вернёт 'queued'. Бэк отдаёт CardSchema (и сводки записей), поэтому
    // stateId/meaningId здесь не знаем — обновятся на следующем refetch (после SSE).
    const mapped = mapBackendCardToState(data, 'B1', []);
    return {
      card: {
        stateId: `pending:${data.id}`,
        cardId: data.id,
        meaningId: null,
        pos: null,
        status: mapped.status,
        payload: {
          front: data.front_name,
          back: '',
          moduleId,
          moduleTitle: '',
          difficulty: 'B1',
        },
        createdAt: Date.now(),
        ready: mapped.ready,
        problem: mapped.problem,
        selectedTranslationIds: [],
        isManual: false,
        isPhrasalVerb: !!data.is_phrasal_verb,
      },
      generations: generationList(data.generations),
    };
  } catch (err) {
    if (err instanceof AxiosError) {
      const parsed = parseRegenerateError(err.response ? err.response.status : null, err.response?.data);
      throw new RegenerateCardError(parsed.code, parsed.quota);
    }
    throw new RegenerateCardError('UNKNOWN');
  }
}

/** Повтор для `/cards/new`: только строка очереди. */
export async function regenerateCard(cardId: string, moduleId: string): Promise<WorkspaceCard> {
  return (await regenerateCardWithGenerations(cardId, moduleId)).card;
}

export interface SaveManualCardArgs {
  front: string;
  /** Переводы карточки (выбранные из словаря и/или свои). Первый — основной. */
  translations: string[];
  moduleId: string;
  moduleTitle: string;
  difficulty: Level;
  /** Id нажатия «Добавить» (`generation_id`); не передан — новый на каждый вызов. */
  generationId?: string;
}

/** Ответ ручной карточки (с «Генерациями»): строка очереди, запись нажатия и признак повтора. */
export interface SaveManualCardResult {
  card: WorkspaceCard;
  generation: GenerationSummary | null;
  replayed: boolean;
  /** Записи, где ручная карточка сняла ожидание своих упавших карточек того же слова (их элементы — «удалена»;
   *  контракт § 7.1, этап 4). `null` — старый бэкенд. */
  released: GenerationSummary[] | null;
  /** Сервер привязал свою готовую карточку слова из другого модуля, введённые переводы не применил (A2-03).
   *  `null` — ответ об этом не сказал (поле `reused` ещё не отдаётся): тогда судят по записи, `isReusedManual`. */
  reused: boolean | null;
  /** Модуль, где слово уже было, — для облачка «Слово уже есть в «…»». Нет в ответе — `null`. */
  reusedModule: { id: string; name: string } | null;
}

/** Модуль `{id, name}` из ответа; чего-то нет — `null`. */
function moduleRef(value: unknown): { id: string; name: string } | null {
  if (!value || typeof value !== 'object') return null;
  const { id, name } = value as { id?: unknown; name?: unknown };
  return typeof id === 'string' && typeof name === 'string' && name.trim() ? { id, name } : null;
}

/**
 * Сохраняет карточку через ручной ввод — без AI, без квот, без worker'а, но
 * ОБОГАЩЁННУЮ словарём: бэк резолвит словарную статью, группирует переводы по частям речи
 * (по meaning + user_card_state на каждую pos), подтягивает транскрипцию/род,
 * собирает MCQ и эмбеддинги. Слова нет в словаре → карточка только с переводами
 * пользователя (один meaning без pos). Статус `manual`, сразу готова к учёбе.
 */
export async function saveManualCard(args: SaveManualCardArgs): Promise<SaveManualCardResult> {
  const front = args.front.trim();
  const translations = args.translations.map((t) => t.trim()).filter(Boolean);
  if (!front || translations.length === 0) throw new CreateCardError('EMPTY_FIELDS');

  type ManualResponse = BackendCard & {
    generation?: unknown;
    replayed?: unknown;
    released?: unknown;
    reused?: unknown;
    reused_module?: unknown;
  };
  let data: ManualResponse;
  try {
    ({ data } = await api.post<ManualResponse>('/cards/manual', {
      module_id: args.moduleId,
      front_name: front,
      translations,
      generation_id: args.generationId ?? newUuid(),
    }));
  } catch (err) {
    // Те же коды, что у создания, кроме жетонов: ручная карточка их не тратит.
    throw toCreateCardError(err);
  }

  // Manual-карточка приходит с уже выбранными переводами —
  // mapBackendCardToState с непустым selectedTranslationIds сразу даёт 'ready'.
  const selectedTranslationIds = data.meanings
    .flatMap((m) => m.senses)
    .flatMap((s) => s.translations)
    .map((t) => t.id);
  const mapped = mapBackendCardToState(data, args.difficulty, selectedTranslationIds);
  // Meanings может быть несколько (по частям речи из словаря) — для локальной
  // строки-превью берём первый; реальные stateId per-pos приедут на refetch
  // progress-cards (POST /manual отдаёт только CardSchema).
  const manualMeaningId = data.meanings[0]?.id ?? null;
  const manualPos = data.meanings[0]?.pos ?? null;

  // Ручная карточка тоже попадает в «уже учится» живого поиска — сбрасываем кэш.
  forgetLookup(front);

  return {
    card: {
      stateId: `manual:${data.id}`,
      cardId: data.id,
      meaningId: manualMeaningId,
      pos: manualPos,
      status: mapped.status,
      payload: {
        front,
        back: pickPrimaryTranslation(data),
        moduleId: args.moduleId,
        moduleTitle: args.moduleTitle,
        difficulty: args.difficulty,
      },
      createdAt: Date.now(),
      ready: mapped.ready,
      problem: mapped.problem,
      selectedTranslationIds,
      isManual: true,
      isPhrasalVerb: !!data.is_phrasal_verb,
    },
    ...generationFields(data),
    released: generationList(data.released),
    reused: typeof data.reused === 'boolean' ? data.reused : null,
    reusedModule: moduleRef(data.reused_module),
  };
}

export async function createCard(args: CreateCardArgs): Promise<WorkspaceCard> {
  return (await createCards(args)).cards[0];
}

/** Карточка ответа POST /cards/create → строка очереди. picks — выбор для этой карточки. */
function mapCreatedCard(
  data: BackendCard,
  front: string,
  picks: { pos: string | null; translation: string }[] | undefined,
  args: CreateCardArgs,
): WorkspaceCard {
  // На POST /cards/create карточка только что заведена, выбор пользователя пуст.
  // Это pending state (meaning_id=NULL) — stateId на бэке есть, но в ответе нет.
  // Используем заглушку `pending:<cardId>` (уникальный per cardId), реальный stateId
  // подтянется на следующем refetch progress-cards.
  const mapped = mapBackendCardToState(data, args.difficulty, []);

  // РЕ-ЮЗ готовой карточки каталога: если /create ответил status='completed',
  // бэкенд УЖЕ выполнил auto_select_all синхронно ДО ответа (обе completed-ветки
  // cards_service.create, включая анти-race перечитку) — job не ставится, SSE не
  // будет. mapped при пустых selectedTranslationIds дал бы 'queued' → карточка
  // зависала «Генерируется» до F5 (прод-инцидент 2026-07-06, слово hope).
  // «completed без выбора» на ЭТОМ ответе невозможен — сразу 'ready'.
  // (Для progress-cards гонка воркер/авто-выбор реальна — mapBackendCardToState
  // там не трогаем.)
  // Исключение — дописывание части речи в готовую карточку (generation_queued):
  // карточка completed, но выбранная строка ждёт генерацию и SSE.
  const status = data.generation_queued
    ? ('queued' as const)
    : data.status === 'completed'
      ? ('ready' as const)
      : mapped.status;
  // Какую строку ждём: первый выбранный перевод без своего смысла в карточке — нет
  // его части речи или он только в «редких» (остальные выбранные уже стали
  // карточками прямо в этом ответе; зеркало pick_sense на бэке).
  const norm = (text: string) => text.trim().toLowerCase();
  const hasOwnSense = (pick: { pos: string | null; translation: string }) =>
    (data.meanings ?? []).some(
      (m) =>
        canonicalPos(m.pos) === canonicalPos(pick.pos) &&
        m.senses.some(
          (s) => !s.is_rare && s.translations.some((t) => norm(t.text) === norm(pick.translation)),
        ),
    );
  const waitingPick = data.generation_queued
    ? (picks?.find((p) => !hasOwnSense(p)) ?? picks?.[0])
    : picks?.[0];

  // Набор «уже учится» для этого слова изменился — выкидываем кэш живого поиска,
  // иначе повторный ввод слова предложит выбрать только что добавленный смысл.
  forgetLookup(front);

  return {
    stateId: `pending:${data.id}`,
    cardId: data.id,
    meaningId: null,
    pos: data.generation_queued ? (waitingPick?.pos ?? null) : null,
    status,
    payload: {
      front,
      // Пока идёт генерация показываем перевод ВЫБРАННОЙ части речи (а не primary
      // карточки — он может быть из другой части речи, которую юзер не выбирал).
      back: waitingPick?.translation || pickPrimaryTranslation(data),
      moduleId: args.moduleId,
      moduleTitle: args.moduleTitle,
      difficulty: args.difficulty,
    },
    createdAt: Date.now(),
    ready: mapped.ready,
    problem: mapped.problem,
    selectedTranslationIds: [],
    isManual: data.status === 'manual',
    isPhrasalVerb: !!data.is_phrasal_verb,
    extending: Boolean(data.generation_queued),
    // Генерируются выбранные переводы без своего смысла в готовой карточке (при
    // дописывании), у новой карточки — все выбранные.
    generatingCount:
      (data.generation_queued
        ? picks?.filter((p) => !hasOwnSense(p)).length
        : picks?.length) || undefined,
    picks: picks?.length ? picks : undefined,
  };
}

export async function createCards(args: CreateCardArgs): Promise<CreateCardsResult> {
  const front = args.front.trim();
  if (!front) throw new CreateCardError('EMPTY_FIELDS');

  if (USE_MOCK) {
    return { cards: [await mockCreateCard({ ...args, front })], skipped: [], generation: null, replayed: false };
  }

  const phrases = (args.phrases ?? []).filter((p) => p.front.trim() && p.picks.length);
  const words = args.topicBatchId ? (args.words ?? []).filter((w) => w.front.trim() && w.picks.length) : [];
  type CreateResponse = BackendCard & {
    phrase_cards?: BackendCard[];
    skipped?: string[];
    generation?: unknown;
    replayed?: unknown;
  };
  let data: CreateResponse;
  try {
    ({ data } = await api.post<CreateResponse>('/cards/create', {
      module_id: args.moduleId,
      front_name: front,
      ...(args.topicBatchId ? { topic_batch_id: args.topicBatchId } : {}),
      // Тело запроса снейк-кейсим руками — интерцептор преобразует только ответы.
      // Выбраны только фразы — пустой selected: карточки слова не будет.
      ...(args.picks?.length || phrases.length || words.length ? { selected: args.picks ?? [] } : {}),
      ...(phrases.length
        ? { phrases: phrases.map((p) => ({ front_name: p.front.trim(), selected: p.picks })) }
        : {}),
      ...(words.length ? { words: words.map((w) => ({ front_name: w.front.trim(), selected: w.picks })) } : {}),
      // Одно нажатие — одна запись «Генераций»; у подбора по теме все пачки шлют один id.
      generation_id: args.generationId ?? newUuid(),
      generation_part: args.generationPart ?? 0,
    }));
  } catch (err) {
    // Серверный гейт NOT_IN_DICTIONARY (опечатка/нет статьи): обычно фронт уже не даёт нажать
    // «Создать», но если запрос ушёл — типизированная ошибка, чтобы предложить ручной ввод.
    throw toCreateCardError(err);
  }
  // Какая карточка какой выбор получила: слово — picks, фраза — свои значения.
  const picksFor = (cardFront: string) => {
    const norm = cardFront.trim().toLowerCase();
    if (norm === front.toLowerCase() && args.picks?.length) return args.picks;
    return [...words, ...phrases].find((p) => p.front.trim().toLowerCase() === norm)?.picks ?? args.picks;
  };
  const all = [data, ...(data.phrase_cards ?? [])];
  // Набор «уже учится» изменился у слова и у каждой фразы — выкидываем кэш живого поиска.
  forgetLookup(front);
  [...phrases, ...words].forEach((p) => forgetLookup(p.front.trim()));
  return {
    cards: all.map((card) => mapCreatedCard(card, card.front_name || front, picksFor(card.front_name || front), args)),
    skipped: data.skipped ?? [],
    ...generationFields(data),
  };
}
